import { Button } from '@/components/ui/button';
import { useDeviceFormCtx, useFormatUpdateDeviceInput } from './DeviceFormProvider';
import { formatDistanceToNow } from 'date-fns';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FetchDeviceQuery, FetchSubDevicesQuery, UpdateDeviceMut } from '@/lib/api/devices/devices';
import { toast } from '@/hooks/use-toast';

export type DeviceSubmitButtonProps = {
  deviceId: string;
  updated?: string;
  onSuccess?: () => void;
};

export const DeviceSubmitButton = (props: DeviceSubmitButtonProps) => {
  const { deviceId, updated } = props;
  const queryClient = useQueryClient();
  const form = useDeviceFormCtx();
  const updateMut = useMutation({
    ...UpdateDeviceMut(deviceId),
    onSuccess() {
      form.reset(form.getValues());
      queryClient.invalidateQueries({ queryKey: FetchDeviceQuery({ deviceId }).queryKey });
      queryClient.invalidateQueries({ queryKey: FetchSubDevicesQuery({ deviceId }).queryKey });
      toast({ title: 'Device saved successfully' });
      props.onSuccess?.();
    },
    onError() {
      toast({ variant: 'destructive', title: 'Could not save device' });
    },
  });
  const formatUpdateInput = useFormatUpdateDeviceInput(deviceId);

  const onSubmit = form.handleSubmit(async (data) => {
    const input = formatUpdateInput(data);
    if (input == null) return;

    updateMut.mutate(input);
  });

  const isDisabled = form.formState.isSubmitting || !form.formState.isDirty || updateMut.isPending;
  const lastUpdatedAt = updateMut.submittedAt || updated;

  return (
    <div className="flex justify-center gap-x-2">
      {lastUpdatedAt && (
        <div className="mr-2 flex items-center gap-4 text-white">
          <p className="text-xs text-gray-400">
            Last updated {formatDistanceToNow(new Date(lastUpdatedAt), { addSuffix: true })}
          </p>
        </div>
      )}
      <Button className="min-w-48" disabled={isDisabled} onClick={onSubmit}>
        Save
      </Button>
    </div>
  );
};
